
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  FormGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { errorMessages,converterJsonToFormData } from '../../utils/methods';
import {getUserMenuById, updateUserMenuAuthRelation} from "../../api/menu";
import { MenuList } from "../../constants/menu";
import QueryString from "qs";

const EmployeeTab3 = ({ employ }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const [selectedMenuAuthIds, setSelectedMenuAuthIds] = useState(''); // 콤마로 구분된 문자열
  const [loading, setLoading] = useState(false);
  const [apiMenuData, setApiMenuData] = useState([]);

  // MenuList를 기반으로 메뉴 데이터 생성
  const menuList = MenuList.flatMap(menu => {
    if (menu.subMenus && menu.subMenus.length > 0) {
      return menu.subMenus.map(subMenu => ({
        id: subMenu.id || subMenu.path.replace(/\//g, ''),
        name: t(subMenu.name),
        path: subMenu.path,
        parentName: t(menu.name),
      }));
    } else {
      return [{
        id: menu.id || menu.path.replace(/\//g, ''),
        name: t(menu.name),
        path: menu.path,
        parentName: null,
      }];
    }
  });

  const getList = async () => {
    const query = {
      page: 1,
      limit: 1000000,
      employeeId: employ.id,
      userId: employ.userId,
    };

    try {
      const qrString = QueryString.stringify(query);
      const data = await getUserMenuById(qrString, user.token);

      //권한관리에서 제외할 권한
      const excludedMenuIds = ['2','5','12','13','14','15','16','17','29','30','31'];

      if (data && data.content) {
        const filteredContent = data.content.filter(item =>
          !excludedMenuIds.includes(item.menuId)
        );

        setApiMenuData(filteredContent);

        // menuAuthYn이 'Y'인 항목들의 menuAuthId를 콤마로 구분된 문자열로 설정
        const checkedMenuAuthIds = filteredContent
          .filter(item => item.menuAuthYn === 'Y')
          .map(item => item.menuAuthId)
          .join(',');

        setSelectedMenuAuthIds(checkedMenuAuthIds);
      }
    } catch (error) {
      console.error('API 호출 실패:', error);
      toast.error(errorMessages(error));
    }
  };

  // path를 기반으로 menuId를 찾는 헬퍼 함수
  const findMenuIdByPath = (path) => {
    const menu = menuList.find(menu => menu.path === path);
    return menu ? menu.id : null;
  };

  useEffect(() => {
    if (employ) {
      getList();
    }
  }, [employ]);

  // 개별 권한 체크박스 변경 처리
  const handlePermissionChange = (menuAuthId, checked) => {
    const currentIds = selectedMenuAuthIds ? selectedMenuAuthIds.split(',').filter(id => id) : [];

    if (checked) {
      // 체크된 경우: 추가
      if (!currentIds.includes(menuAuthId)) {
        currentIds.push(menuAuthId);
      }
    } else {
      // 체크 해제된 경우: 제거
      const index = currentIds.indexOf(menuAuthId);
      if (index > -1) {
        currentIds.splice(index, 1);
      }
    }

    setSelectedMenuAuthIds(currentIds.join(','));
  };

  // 전체 선택/해제 처리
  const handleSelectAll = (menuId, checked) => {
    const availablePermissions = getAvailablePermissions(menuId);
    const currentIds = selectedMenuAuthIds ? selectedMenuAuthIds.split(',').filter(id => id) : [];

    availablePermissions.forEach(permission => {
      const menuAuthId = permission.menuAuthId;
      const index = currentIds.indexOf(menuAuthId);

      if (checked) {
        // 전체 선택: 없으면 추가
        if (index === -1) {
          currentIds.push(menuAuthId);
        }
      } else {
        // 전체 해제: 있으면 제거
        if (index > -1) {
          currentIds.splice(index, 1);
        }
      }
    });

    setSelectedMenuAuthIds(currentIds.join(','));
  };

  // 해당 메뉴에 사용 가능한 권한들을 가져오는 함수
  const getAvailablePermissions = (menuId) => {
    const availablePermissions = [];

    apiMenuData.forEach(item => {
      const currentMenuId = findMenuIdByPath(item.path) || item.menuId;
      if (currentMenuId === menuId) {
        availablePermissions.push({
          menuAuthId: item.menuAuthId,
          authCode: item.authCode,
          authName: item.authName
        });
      }
    });

    // 중복 제거 (menuAuthId 기준)
    const uniquePermissions = availablePermissions.filter(
      (permission, index, self) =>
        index === self.findIndex(p => p.menuAuthId === permission.menuAuthId)
    );

    return uniquePermissions;
  };

  // 개별 권한이 체크되어 있는지 확인
  const isPermissionChecked = (menuAuthId) => {
    const currentIds = selectedMenuAuthIds ? selectedMenuAuthIds.split(',') : [];
    return currentIds.includes(menuAuthId);
  };

  // 전체 선택 상태 확인
  const isAllSelected = (menuId) => {
    const availablePermissions = getAvailablePermissions(menuId);
    if (availablePermissions.length === 0) return false;

    return availablePermissions.every(permission =>
      isPermissionChecked(permission.menuAuthId)
    );
  };

  // 중간 상태 확인 (일부만 선택됨)
  const isIndeterminate = (menuId) => {
    const availablePermissions = getAvailablePermissions(menuId);
    if (availablePermissions.length === 0) return false;

    const selectedCount = availablePermissions.filter(permission =>
      isPermissionChecked(permission.menuAuthId)
    ).length;

    return selectedCount > 0 && selectedCount < availablePermissions.length;
  };

  const handleSavePermissions = async () => {
    setLoading(true);
    try {
      // 실제 API 호출로 권한 저장
      // await saveEmployeePermissions(employ.id, selectedMenuAuthIds, user.token);

      const permissionData = {
        employeeId: employ?.id,
        userId: employ?.userId,
        menuAuthIds: selectedMenuAuthIds,
      };

      await updateUserMenuAuthRelation(permissionData,user.token);

      toast.success(t('권한이 성공적으로 저장되었습니다'));
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {t('권한 관리')}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {employ?.name}님의 메뉴별 권한을 설정해주세요
      </Typography>

      <Grid container spacing={2}>
        {menuList.map((menu) => {
          const availablePermissions = getAvailablePermissions(menu.id);

          // 해당 메뉴에 권한이 없으면 표시하지 않음
          if (availablePermissions.length === 0) {
            return null;
          }

          return (
            <Grid item xs={12} key={menu.id}>
              <Card
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* 메뉴명 */}
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {menu.name}
                      </Typography>
                      {menu.parentName && (
                        <Typography variant="caption" color="text.secondary">
                          {menu.parentName}
                        </Typography>
                      )}
                    </Grid>

                    {/* 권한 체크박스들 */}
                    <Grid item xs={12} md={9}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                          p: 2,
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          alignItems: 'center',
                        }}
                      >
                        {/* 전체 선택 체크박스 */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isAllSelected(menu.id)}
                              indeterminate={isIndeterminate(menu.id)}
                              onChange={(e) => handleSelectAll(menu.id, e.target.checked)}
                              sx={{
                                color: '#0F7F85',
                                '&.Mui-checked': { color: '#0F7F85' },
                                '&.MuiCheckbox-indeterminate': { color: '#0F7F85' },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: '#333', fontWeight: 'medium' }}>
                              전체
                            </Typography>
                          }
                        />

                        {/* 구분선 */}
                        <Box sx={{ height: '24px', width: '1px', backgroundColor: '#ddd', mx: 1 }} />

                        {/* 개별 권한 체크박스들 */}
                        {availablePermissions.map((permission) => (
                          <FormControlLabel
                            key={permission.menuAuthId}
                            control={
                              <Checkbox
                                checked={isPermissionChecked(permission.menuAuthId)}
                                onChange={(e) =>
                                  handlePermissionChange(permission.menuAuthId, e.target.checked)
                                }
                                sx={{
                                  color: '#0F7F85',
                                  '&.Mui-checked': { color: '#0F7F85' },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                {permission.authName}
                              </Typography>
                            }
                          />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* 저장 버튼 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSavePermissions}
          disabled={loading}
          sx={{
            backgroundColor: '#0F7F85',
            '&:hover': { backgroundColor: '#0A585C' },
            borderRadius: '8px',
            px: 3,
            py: 1.5,
          }}
        >
          {loading ? t('저장 중...') : t('저장')}
        </Button>
      </Box>
    </Box>
  );
};

export default EmployeeTab3;